import { Alert, Form, Input, Modal, Select, message } from "antd";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import usersService from "../../services/users.service";
import { UserRole, resolveRole } from "../../services/roles";
import { IUser } from "../../models/user";
import { useI18n } from "../../i18n/I18nContext";

interface IUserCreateModal {
  isModalOpen: boolean;
  closeModal: () => void;
}

const OperatorCreate = (props: IUserCreateModal) => {
  const { isModalOpen, closeModal } = props;
  const { t } = useI18n();
  const [form] = Form.useForm();
  const [error, setError] = useState<string>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const currentUser = useSelector(
    ({ UserSlice }: any) => UserSlice.user as IUser
  );
  const actorRole = resolveRole(currentUser?.role);

  const roleOptions = useMemo(() => {
    if (actorRole === "admin") {
      return [
        { value: "admin", label: t("users.role.admin") },
        { value: "manager", label: t("users.role.manager") },
        { value: "operator", label: t("users.role.operator") },
      ];
    }
    // FIX-002: manager создаёт только operator
    return [{ value: "operator", label: t("users.role.operator") }];
  }, [actorRole, t]);

  const handleCancel = () => {
    setError(undefined);
    setCreatedToken(null);
    form.resetFields();
    closeModal();
  };

  const handleOk = () => {
    if (createdToken) {
      handleCancel();
      window.location.reload();
      return;
    }

    form
      .validateFields()
      .then((values) => createUser(values))
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const createUser = async (values: {
    username: string;
    password: string;
    role: UserRole;
  }) => {
    setConfirmLoading(true);
    setError(undefined);

    try {
      const res = await usersService.createUser(values);
      if (res?._id) {
        setCreatedToken(res.tokenFromFlashDrive || null);
        if (!res.tokenFromFlashDrive) {
          window.location.reload();
        }
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Ошибка при создании пользователя";
      const text = Array.isArray(msg) ? msg.join(", ") : String(msg);
      setError(text);
      message.error(text, 8);
    } finally {
      setConfirmLoading(false);
    }
  };

  const downloadToken = () => {
    if (!createdToken) return;
    const blob = new Blob([createdToken], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.getFieldValue("username") || "user"}-token.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      title={t("users.create")}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={createdToken ? "OK" : t("users.create")}
      cancelText="Cancel"
      confirmLoading={confirmLoading}
    >
      {createdToken ? (
        <div>
          <Alert
            type="success"
            showIcon
            message="OK"
            description="Token"
            style={{ marginBottom: 16 }}
          />
          <Input.TextArea value={createdToken} rows={3} readOnly />
          <a
            onClick={downloadToken}
            style={{ display: "inline-block", marginTop: 12 }}
          >
            token.txt
          </a>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          name="form_in_modal"
          initialValues={{ role: "operator" }}
        >
          <Form.Item
            name="username"
            label="Login"
            rules={[{ required: true }]}
          >
            <Input type="email" placeholder="example@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true },
              { min: 6 },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={roleOptions} />
          </Form.Item>
        </Form>
      )}

      {error && (
        <Alert type="error" message={error} style={{ marginTop: 12 }} />
      )}
    </Modal>
  );
};

export default OperatorCreate;
